/**
 * Quiz « Trouver mon couteau parfait »
 * Funnel 5 questions → recommandation vers collections / produits réels.
 */
(() => {
  const EUR = new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
  });

  const USAGES = [
    {
      id: 'all',
      label: 'POLYVALENT - un peu de tout',
      types: ['santoku', 'chef'],
    },
    {
      id: 'veg',
      label: 'Légumes, herbes & aromates',
      types: ['santoku', 'nakiri'],
    },
    {
      id: 'meat',
      label: 'Viande, volaille & rôtis',
      types: ['chef', 'trancher', 'desosser'],
    },
    {
      id: 'fish',
      label: 'Poisson & découpes fines',
      types: ['kiritsuke', 'trancher'],
    },
    {
      id: 'detail',
      label: 'Pain & petits travaux',
      types: ['office', 'utilitaire', 'pain'],
    },
  ];

  const FINISHES = {
    prestige: {
      id: 'prestige',
      title: 'Gamme Prestige Damas Densité Maximale',
      tag: 'chef-yakumoto',
      tags: ['chef-yakumoto', 'mer-jaune'],
    },
    tradition: {
      id: 'tradition',
      title: 'Gamme Tradition en Damas 67 Couches',
      tag: 'kaito',
    },
    standard: {
      id: 'standard',
      title: 'Gamme Standard en Acier',
      tag: 'chef-tanaka',
    },
  };

  const SET_ORDER = [2, 3, 5, 7, 10];
  const SET_LABELS = {
    2: 'Duo',
    3: 'Set Essentiel',
    5: 'Set Complet',
    7: 'Set Expert',
    10: 'Master Set',
  };

  /** Fallback si le JSON Liquid n’est pas là. Prix = minimum réel par gamme / pièces. */
  const FALLBACK_SET_ROWS = [
    { finish: 'standard', pieces: 3, handle: 'set-de-3-couteaux-chef-tanaka-mrg', title: 'Coffret Chef Tanaka · 3 couteaux', cents: 9999 },
    { finish: 'standard', pieces: 5, handle: 'set-de-5-couteaux-chef-tanaka-mrg', title: 'Coffret Chef Tanaka · 5 couteaux', cents: 13999 },
    { finish: 'standard', pieces: 7, handle: 'set-de-7-couteaux-chef-tanaka-mrg', title: 'Coffret Chef Tanaka · 7 couteaux', cents: 19999 },
    { finish: 'standard', pieces: 10, handle: 'set-de-10-couteaux-chef-tanaka-mrg', title: 'Coffret Chef Tanaka · 10 couteaux', cents: 22999 },
    { finish: 'tradition', pieces: 3, handle: 'set-de-3-couteaux-kaito-mrg', title: 'Coffret Kaito · 3 couteaux', cents: 19999 },
    { finish: 'tradition', pieces: 5, handle: 'set-de-5-couteaux-kaito-mrg', title: 'Coffret Kaito · 5 couteaux', cents: 29999 },
    { finish: 'tradition', pieces: 7, handle: 'set-de-7-couteaux-kaito-mrg', title: 'Coffret Kaito · 7 couteaux', cents: 39999 },
    { finish: 'tradition', pieces: 10, handle: 'set-de-10-couteaux-kaito-mrg', title: 'Coffret Kaito · 10 couteaux', cents: 49999 },
    { finish: 'prestige', pieces: 2, handle: 'set-de-2-couteaux-mer-jaune-mrg', title: 'Coffret Mer Profonde · 2 couteaux', cents: 18999 },
    { finish: 'prestige', pieces: 3, handle: 'set-de-3-couteaux-chef-yakumoto-mrg', title: 'Coffret Chef Yakumoto · 3 couteaux', cents: 24999 },
    { finish: 'prestige', pieces: 3, handle: 'set-de-3-couteaux-mer-jaune-mrg', title: 'Coffret Mer Profonde · 3 couteaux', cents: 27999 },
    { finish: 'prestige', pieces: 5, handle: 'set-de-5-couteaux-chef-yakumoto-mrg', title: 'Coffret Chef Yakumoto · 5 couteaux', cents: 32999 },
    { finish: 'prestige', pieces: 5, handle: 'set-de-5-couteaux-fibre-de-carbone-japonais', title: 'Coffret Fibre de Carbone · 5 couteaux', cents: 37990 },
    { finish: 'prestige', pieces: 7, handle: 'set-de-7-couteaux-mer-jaune-mrg', title: 'Coffret Mer Profonde · 7 couteaux', cents: 54999 },
  ];

  function buildCatalog(rows) {
    const catalog = { standard: {}, tradition: {}, prestige: {} };
    (Array.isArray(rows) ? rows : []).forEach((row) => {
      if (!row || !catalog[row.finish] || !row.handle) return;
      const pieces = Number(row.pieces);
      const cents = Number(row.cents);
      if (!pieces || !Number.isFinite(cents)) return;
      const bucket = catalog[row.finish][pieces] || { cents, products: [] };
      bucket.products.push({
        handle: row.handle,
        title: row.title || row.handle,
        cents,
        url: row.url || '',
      });
      bucket.products.sort((a, b) => a.cents - b.cents);
      bucket.cents = bucket.products[0].cents;
      catalog[row.finish][pieces] = bucket;
    });
    return catalog;
  }

  function escapeHtml(value) {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function uniqueTypes(usageIds) {
    const seen = new Set();
    const types = [];
    usageIds.forEach((id) => {
      const usage = USAGES.find((item) => item.id === id);
      (usage?.types || []).forEach((type) => {
        if (!seen.has(type)) {
          seen.add(type);
          types.push(type);
        }
      });
    });
    return types;
  }

  class MrgQuiz extends HTMLElement {
    /** @type {HTMLDialogElement | null} */
    #dialog = null;
    /** @type {HTMLElement | null} */
    #trigger = null;
    #stepIndex = 0;
    #state = this.#blankState();
    #types = {};
    #catalog = buildCatalog(FALLBACK_SET_ROWS);
    #showingResults = false;
    #advanceTimer = 0;

    #blankState() {
      return {
        destination: '',
        usages: [],
        format: '',
        finish: '',
        pieces: 0,
      };
    }

    connectedCallback() {
      this.#dialog = this.querySelector('dialog');
      if (!this.#dialog) return;

      const typesEl = this.querySelector('[data-mrg-quiz-types]');
      if (typesEl) {
        try {
          this.#types = JSON.parse(typesEl.textContent || '{}');
        } catch {
          this.#types = {};
        }
      }

      const setsEl = this.querySelector('[data-mrg-quiz-sets]');
      if (setsEl) {
        try {
          const rows = JSON.parse(setsEl.textContent || '[]');
          if (Array.isArray(rows) && rows.length) this.#catalog = buildCatalog(rows);
        } catch {
          this.#catalog = buildCatalog(FALLBACK_SET_ROWS);
        }
      }

      this.querySelector('[data-mrg-quiz-close]')?.addEventListener('click', () => this.close());
      this.querySelector('[data-mrg-quiz-back]')?.addEventListener('click', () => this.#back());
      this.querySelector('[data-mrg-quiz-next]')?.addEventListener('click', () => this.#next());

      this.#dialog.addEventListener('click', (event) => {
        if (event.target === this.#dialog) this.close();
      });
      this.#dialog.addEventListener('cancel', (event) => {
        event.preventDefault();
        this.close();
      });
      this.#dialog.addEventListener('close', () => this.#onNativeClose());

      this.addEventListener('change', (event) => this.#onFieldChange(event));
      this.addEventListener('click', (event) => this.#onOptionClick(event));
      this.addEventListener('keydown', (event) => this.#onKeydown(event));
    }

    disconnectedCallback() {
      this.#releasePage();
    }

    open(trigger) {
      if (!this.#dialog) this.#dialog = this.querySelector('dialog');
      if (!this.#dialog || typeof this.#dialog.showModal !== 'function') return;
      if (this.#dialog.open) return;

      this.#trigger = trigger instanceof HTMLElement ? trigger : null;
      this.#state = this.#blankState();
      this.#stepIndex = 0;
      this.#showingResults = false;
      document.body.classList.add('mrg-quiz-open');
      document.documentElement.setAttribute('scroll-lock', '');
      this.#render();
      this.#dialog.showModal();
      this.#bindPageGuard(true);
      this.#focusBody();
    }

    close() {
      if (!this.#dialog?.open) return;
      this.#dialog.close();
    }

    #onNativeClose() {
      this.#clearAdvance();
      this.#releasePage();
      this.#state = this.#blankState();
      this.#stepIndex = 0;
      this.#showingResults = false;
      const trigger = this.#trigger;
      this.#trigger = null;
      if (trigger && typeof trigger.focus === 'function') {
        trigger.focus({ preventScroll: true });
      }
    }

    #bindPageGuard(on) {
      const opts = { capture: true, passive: false };
      if (on) {
        document.addEventListener('wheel', this.#onBlockPageScroll, opts);
        document.addEventListener('touchmove', this.#onBlockPageScroll, opts);
      } else {
        document.removeEventListener('wheel', this.#onBlockPageScroll, opts);
        document.removeEventListener('touchmove', this.#onBlockPageScroll, opts);
      }
    }

    #onBlockPageScroll = (event) => {
      if (!this.#dialog?.open) return;

      const raw = event.target;
      const el = raw instanceof Element ? raw : raw instanceof Node ? raw.parentElement : null;
      const body = this.querySelector('[data-mrg-quiz-body]');
      const insideBody = Boolean(body instanceof HTMLElement && el && body.contains(el));

      if (insideBody && body instanceof HTMLElement && body.scrollHeight > body.clientHeight + 1) {
        if (event instanceof WheelEvent) {
          const atTop = body.scrollTop <= 0 && event.deltaY < 0;
          const atBottom = body.scrollTop + body.clientHeight >= body.scrollHeight - 1 && event.deltaY > 0;
          if (atTop || atBottom) event.preventDefault();
        }
        return;
      }

      event.preventDefault();
    };

    #releasePage() {
      this.#bindPageGuard(false);
      document.documentElement.removeAttribute('scroll-lock');
      document.body.classList.remove('mrg-quiz-open');
    }

    #steps() {
      const steps = ['destination', 'usages', 'format', 'finish'];
      if (this.#state.format === 'set') steps.push('pieces');
      return steps;
    }

    #currentStep() {
      return this.#steps()[this.#stepIndex] || 'destination';
    }

    #questionCount() {
      return this.#steps().length;
    }

    #isInstantStep() {
      const step = this.#currentStep();
      return step === 'destination' || step === 'format' || step === 'finish' || step === 'pieces';
    }

    #clearAdvance() {
      window.clearTimeout(this.#advanceTimer);
      this.#advanceTimer = 0;
    }

    #onFieldChange(event) {
      const input = event.target;
      if (!(input instanceof HTMLInputElement)) return;

      if (input.name === 'destination') this.#state.destination = input.value;
      if (input.name === 'format') {
        this.#state.format = input.value;
        if (input.value !== 'set') this.#state.pieces = 0;
      }
      if (input.name === 'finish') {
        this.#state.finish = input.value;
        this.#state.pieces = 0;
      }
      if (input.name === 'pieces') this.#state.pieces = Number(input.value) || 0;
      if (input.name === 'usages') {
        this.#state.usages = Array.from(this.querySelectorAll('input[name="usages"]:checked')).map(
          (el) => /** @type {HTMLInputElement} */ (el).value
        );
      }

      this.#syncSelected();
      this.#syncFooter();
      this.#clearError();
    }

    #onOptionClick(event) {
      if (!(event.target instanceof Element)) return;
      const option = event.target.closest('.mrg-quiz__option');
      if (!option || !this.contains(option)) return;
      const input = option.querySelector('input[type="radio"]');
      if (!(input instanceof HTMLInputElement)) return;
      if (!this.#isInstantStep()) return;

      this.#clearError();
      this.#clearAdvance();
      this.#advanceTimer = window.setTimeout(() => this.#next(), 160);
    }

    #onKeydown(event) {
      if (event.key !== 'Enter') return;
      if (event.target instanceof HTMLButtonElement) return;
      if (this.#isInstantStep()) return;
      event.preventDefault();
      this.#next();
    }

    #back() {
      this.#clearAdvance();
      if (this.#showingResults) {
        this.#showingResults = false;
        this.#render();
        this.#focusBody();
        return;
      }
      if (this.#stepIndex === 0) {
        this.close();
        return;
      }
      this.#stepIndex -= 1;
      this.#render();
      this.#focusBody();
    }

    #next() {
      if (!this.#validate()) return;

      if (this.#stepIndex >= this.#steps().length - 1) {
        const matches = this.#matchingProducts();
        if (this.#state.format === 'set' && matches.length > 1) {
          this.#showingResults = true;
          this.#render();
          this.#focusBody();
          return;
        }
        window.location.href = this.#exitUrl();
        return;
      }

      this.#stepIndex += 1;
      this.#render();
      this.#focusBody();
    }

    #validate() {
      const step = this.#currentStep();
      const messages = {
        destination: 'Indiquez à qui s’adresse cette commande.',
        usages: 'Sélectionnez au moins une découpe.',
        format: 'Choisissez un format.',
        finish: 'Choisissez un niveau de finition.',
        pieces: 'Choisissez le nombre de pièces.',
      };
      const ok = {
        destination: Boolean(this.#state.destination),
        usages: this.#state.usages.length > 0,
        format: Boolean(this.#state.format),
        finish: Boolean(this.#state.finish),
        pieces: this.#state.pieces > 0,
      }[step];

      if (ok) {
        this.#clearError();
        return true;
      }
      this.#showError(messages[step] || 'Complétez cette étape pour continuer.');
      return false;
    }

    #showError(message) {
      const el = this.querySelector('[data-mrg-quiz-error]');
      if (!el) return;
      el.hidden = false;
      el.textContent = message;
    }

    #clearError() {
      const el = this.querySelector('[data-mrg-quiz-error]');
      if (!el) return;
      el.hidden = true;
      el.textContent = '';
    }

    #focusBody() {
      const body = this.querySelector('[data-mrg-quiz-body]');
      if (body instanceof HTMLElement) body.focus({ preventScroll: true });
    }

    #typeUrl(type) {
      return this.#types[type] || this.dataset.urlKnives || '/collections/couteau-japonais';
    }

    #seriesTag() {
      const finish = FINISHES[this.#state.finish] || FINISHES.tradition;
      return finish.tag;
    }

    #finishCollectionUrl() {
      if (this.#state.finish === 'standard') {
        return this.dataset.urlStandard || '/collections/gamme-standard';
      }
      if (this.#state.finish === 'tradition') {
        return this.dataset.urlTradition || '/collections/gamme-tradition';
      }
      if (this.#state.finish === 'prestige') {
        return this.dataset.urlPrestige || '/collections/gamme-prestige';
      }
      return this.dataset.urlSets || '/collections/sets-de-couteaux-japonais';
    }

    #withTag(path, tag) {
      const clean = String(path || '').replace(/\/$/, '');
      if (!tag) return clean;
      return `${clean}/${tag}`;
    }

    #setEntry() {
      const finish = this.#state.finish || 'tradition';
      const group = this.#catalog[finish] || {};
      return group[this.#state.pieces] || null;
    }

    #matchingProducts() {
      return this.#setEntry()?.products || [];
    }

    #productUrl(handle) {
      const root = String(this.dataset.root || '/').replace(/\/$/, '');
      return `${root}/products/${handle}`;
    }

    #productHref(product) {
      if (product?.url) return product.url;
      if (product?.handle) return this.#productUrl(product.handle);
      return this.dataset.urlSets || '/collections/sets-de-couteaux-japonais';
    }

    #exitUrl() {
      if (this.#state.format === 'set') {
        const products = this.#matchingProducts();
        if (products.length) return this.#productHref(products[0]);
        return this.dataset.urlSets || '/collections/sets-de-couteaux-japonais';
      }

      const types = uniqueTypes(this.#state.usages);
      if (types.length === 1) {
        const typeUrl = this.#typeUrl(types[0]);
        if (this.#state.finish === 'prestige') return typeUrl;
        return this.#withTag(typeUrl, this.#seriesTag());
      }

      return this.#finishCollectionUrl();
    }

    #render() {
      const step = this.#currentStep();
      const questionCount = this.#questionCount();
      const questionNumber = this.#showingResults ? questionCount : this.#stepIndex + 1;
      const progress = this.#showingResults ? 100 : Math.round((questionNumber / questionCount) * 100);

      const bar = this.querySelector('[data-mrg-quiz-progress]');
      if (bar instanceof HTMLElement) bar.style.width = `${progress}%`;

      const indexEl = this.querySelector('[data-mrg-quiz-index]');
      if (indexEl) {
        indexEl.textContent = this.#showingResults
          ? 'Votre sélection'
          : `${questionNumber} / ${questionCount}`;
      }

      const body = this.querySelector('[data-mrg-quiz-body]');
      if (body) body.innerHTML = this.#showingResults ? this.#resultsHtml() : this.#bodyHtml(step);

      this.#syncSelected();
      this.#syncFooter();
    }

    #syncSelected() {
      this.querySelectorAll('.mrg-quiz__option').forEach((label) => {
        const input = label.querySelector('input');
        label.classList.toggle('is-selected', Boolean(input instanceof HTMLInputElement && input.checked));
      });
    }

    #syncFooter() {
      const footer = this.querySelector('.mrg-quiz__footer');
      const back = this.querySelector('[data-mrg-quiz-back]');
      const next = this.querySelector('[data-mrg-quiz-next]');
      const showBack = this.#stepIndex > 0;
      const instant = this.#isInstantStep() && !this.#showingResults;

      if (back instanceof HTMLElement) back.hidden = !showBack && !this.#showingResults;
      if (next instanceof HTMLButtonElement) {
        if (instant || this.#showingResults) {
          next.hidden = true;
        } else {
          next.hidden = false;
          next.disabled = false;
          next.textContent = 'Continuer';
        }
      }

      if (footer instanceof HTMLElement) {
        const nextHidden = !(next instanceof HTMLButtonElement) || next.hidden;
        footer.hidden = !showBack && nextHidden;
      }
    }

    #bodyHtml(step) {
      switch (step) {
        case 'destination':
          return this.#questionHtml('À qui s’adresse cette commande ?', [
            this.#radio('destination', 'self', 'Pour moi-même'),
            this.#radio('destination', 'gift', 'Pour offrir'),
          ]);
        case 'usages':
          return this.#questionHtml(
            'Quelles découpes préparez-vous le plus souvent ? (Sélectionnez tout ce qui s’applique)',
            USAGES.map((usage) => this.#check('usages', usage.id, usage.label, usage.badge))
          );
        case 'format':
          return this.#questionHtml('Quel format souhaitez-vous pour votre cuisine ?', [
            this.#radio('format', 'solo', 'Un couteau seul'),
            this.#radio('format', 'set', 'Un Set Culinaire Japonais / Pack Cadeau'),
          ]);
        case 'finish':
          return this.#questionHtml(
            'Quel niveau de finition recherchez-vous pour votre ensemble ?',
            Object.values(FINISHES).map((finish) => this.#radio('finish', finish.id, finish.title))
          );
        case 'pieces':
          return this.#piecesHtml();
        default:
          return '';
      }
    }

    #resultsHtml() {
      const finish = FINISHES[this.#state.finish] || FINISHES.tradition;
      const products = this.#matchingProducts();
      const pieces = this.#state.pieces;
      const cards = products
        .map(
          (product) => `
            <a class="mrg-quiz__result" href="${escapeHtml(this.#productHref(product))}">
              <span class="mrg-quiz__option-text">
                <strong>${escapeHtml(product.title)}</strong>
              </span>
              <span class="mrg-quiz__price">${EUR.format(product.cents / 100)}</span>
            </a>
          `
        )
        .join('');

      return `
        <h3 class="mrg-quiz__question">${escapeHtml(finish.title)} · ${pieces} pièces</h3>
        <p class="mrg-quiz__hint">Tous les ensembles qui correspondent, du plus accessible au plus travaillé.</p>
        <div class="mrg-quiz__options" role="list">${cards}</div>
      `;
    }

    #questionHtml(title, options) {
      return `
        <h3 class="mrg-quiz__question">${escapeHtml(title)}</h3>
        <div class="mrg-quiz__options" role="group">${options.join('')}</div>
        <p class="mrg-quiz__error" data-mrg-quiz-error hidden></p>
      `;
    }

    #radio(name, value, title) {
      const checked = this.#state[name] === value ? ' checked' : '';
      return `
        <label class="mrg-quiz__option">
          <input type="radio" name="${escapeHtml(name)}" value="${escapeHtml(value)}"${checked}>
          <span class="mrg-quiz__check mrg-quiz__radio" aria-hidden="true"></span>
          <span class="mrg-quiz__option-text">
            <strong>${escapeHtml(title)}</strong>
          </span>
        </label>
      `;
    }

    #check(name, value, title, badge) {
      const checked = this.#state.usages.includes(value) ? ' checked' : '';
      const badgeHtml = badge
        ? `<span class="mrg-quiz__badge">${escapeHtml(badge)}</span>`
        : '';
      return `
        <label class="mrg-quiz__option">
          <input type="checkbox" name="${escapeHtml(name)}" value="${escapeHtml(value)}"${checked}>
          <span class="mrg-quiz__check" aria-hidden="true"></span>
          <span class="mrg-quiz__option-text">
            <strong>${escapeHtml(title)}</strong>
            ${badgeHtml}
          </span>
        </label>
      `;
    }

    #piecesHtml() {
      const finish = FINISHES[this.#state.finish] || FINISHES.tradition;
      const catalog = this.#catalog[finish.id] || {};
      const options = SET_ORDER.filter((n) => catalog[n]?.products?.length).map((n) => {
        const set = catalog[n];
        const checked = this.#state.pieces === n ? ' checked' : '';
        const from = set.products.length > 1 ? '<em>à partir de</em> ' : '';
        return `
          <label class="mrg-quiz__option mrg-quiz__option--price">
            <input type="radio" name="pieces" value="${n}"${checked}>
            <span class="mrg-quiz__check mrg-quiz__radio" aria-hidden="true"></span>
            <span class="mrg-quiz__option-text">
              <strong>${escapeHtml(SET_LABELS[n] || 'Set')} (${n} couteaux)</strong>
            </span>
            <span class="mrg-quiz__price">${from}${EUR.format(set.cents / 100)}</span>
          </label>
        `;
      });

      return this.#questionHtml(
        'Combien de pièces souhaitez-vous intégrer dans votre ensemble ?',
        options
      );
    }
  }

if (!customElements.get('mrg-quiz')) {
  customElements.define('mrg-quiz', MrgQuiz);
}

document.addEventListener(
  'click',
  (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const trigger = target.closest('[data-mrg-quiz-open]');
    if (!trigger) return;
    const quiz = document.querySelector('mrg-quiz');
    if (!quiz || typeof quiz.open !== 'function') return;
    event.preventDefault();
    event.stopPropagation();
    quiz.open(trigger instanceof HTMLElement ? trigger : null);
  },
  true
);
})();
